/* global __APP_VERSION__ */

import { useLayoutEffect, useRef } from "react"

import clsx from "clsx"
import useSWR from "swr"

import { s } from "@/utils/i18n"

import useTabHidden from "@/hooks/useTabHidden"
import useToast from "@/hooks/useToast"

import { Button } from "@/components/Button"
import { H6 } from "@/components/TypeRamp"

import ArrowsClockwiseIcon from "@/icons/ArrowsClockwise"

/**
 * Polls client code version against deployed code to check for mismatches and shows a nag to refresh if out of date
 */
export default function VersionManager() {
  const isTabHidden = useTabHidden()
  const localVersion = typeof __APP_VERSION__ === "undefined" ? "" : __APP_VERSION__

  const { data, isLoading } = useSWR(
    import.meta.env.PROD ? "/version.json" : null,
    (r) => fetch(r, { headers: { "Cache-Control": "no-cache" } }).then((res) => res.json()),
    // 90 seconds
    { refreshInterval: !isTabHidden ? 90_000 : undefined }
  )
  const { addToast } = useToast()
  const notifiedRef = useRef(false)

  useLayoutEffect(() => {
    if (!import.meta.env.PROD || isLoading || notifiedRef.current) {
      return
    }

    if (localVersion === data?.version) {
      return
    }
    console.info(
      `%c⚠️ New version available: %c${data?.version}`,
      "color: goldenrod;",
      "font-weight: 700; color: darkgoldenrod"
    )

    addToast(
      {
        message: (
          <>
            <p className="mt-2">{s`App updated`}</p>
            <p className="mt-2">{s`Press 'Reload' to continue`}</p>
          </>
        ),
        detail: (
          <Button
            as="button"
            size="custom"
            theme="secondary"
            type="button"
            onClick={() => {
              window.location.reload()
            }}
            className={clsx(H6.classNames, "mt-2 p-3")}
          >
            <ArrowsClockwiseIcon width="16" height="16" />
            {s`Reload`}
          </Button>
        ),
        permanent: true,
      },
      "none"
    )
    notifiedRef.current = true
  }, [addToast, localVersion, data, isLoading])

  return null
}
